import { useEffect, useState } from 'react';
import storage from '../../../hooks/useStorage/useStorage';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';

const MAX_RECENT_SEARCHES = 5;

const readRecentSearches = (teamId: string): string[] => {
  const stored = storage.getGlobalSearchRecents(teamId);
  if (!Array.isArray(stored)) {
    return [];
  }
  return stored.filter((entry): entry is string => typeof entry === 'string');
};

const persistRecentSearches = (teamId: string, searches: string[]): string[] => {
  storage.setGlobalSearchRecents(teamId, searches);
  return searches;
};

const useRecentSearches = () => {
  const { team } = useApplicationContext();
  const teamId = team?.id;

  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  useEffect(() => {
    if (!teamId) {
      setRecentSearches([]);
      return;
    }
    setRecentSearches(readRecentSearches(teamId));
  }, [teamId]);

  const addRecentSearch = (rawQuery: string) => {
    if (!teamId) {
      return;
    }
    const query = rawQuery.trim();
    if (!query) {
      return;
    }
    setRecentSearches((previous) => {
      const withoutDuplicate = previous.filter(
        (entry) => entry.toLowerCase() !== query.toLowerCase(),
      );
      return persistRecentSearches(
        teamId,
        [query, ...withoutDuplicate].slice(0, MAX_RECENT_SEARCHES),
      );
    });
  };

  const removeRecentSearch = (query: string) => {
    if (!teamId) {
      return;
    }
    setRecentSearches((previous) =>
      persistRecentSearches(
        teamId,
        previous.filter((entry) => entry !== query),
      ),
    );
  };

  return { recentSearches, addRecentSearch, removeRecentSearch };
};

export default useRecentSearches;
